import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { caseStudies } from '../mockData';
import { Building2, Factory, Briefcase } from 'lucide-react';

export const PortfolioPage = () => {
  const getIndustryIcon = (industry) => {
    switch (industry.toLowerCase()) {
      case 'manufacturing':
        return <Factory className="w-5 h-5" />;
      case 'commercial':
        return <Building2 className="w-5 h-5" />;
      case 'industrial':
        return <Briefcase className="w-5 h-5" />;
      default:
        return <Briefcase className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20">
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 font-heading">
            Portfolio & Case Studies
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover how we've helped businesses across various industries achieve operational excellence through our innovative control panel solutions
          </p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study) => (
              <Card
                key={study.id}
                className="overflow-hidden border-2 hover:border-[#004494] transition-all duration-300 card-hover cursor-pointer group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#004494]/90 via-[#004494]/50 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-[#FFD617] text-[#004494] hover:bg-[#FFD617]/90">
                      <span className="mr-2">{getIndustryIcon(study.industry)}</span>
                      {study.industry}
                    </Badge>
                  </div>
                </div>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading">
                    {study.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3 font-medium">{study.client}</p>
                  <p className="text-gray-600 leading-relaxed">{study.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Projects Placeholder */}
          <div className="mt-16 text-center">
            <Card className="bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-300">
              <CardContent className="py-16 px-8">
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading">
                    More Projects Coming Soon
                  </h3>
                  <p className="text-gray-600 mb-8">
                    We're constantly working on exciting new projects. Check back soon to see our latest work in industrial automation and control systems.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-block bg-[#004494] hover:bg-[#003370] text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Discuss Your Project
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-4 sm:px-6 lg:px-8 mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#004494] rounded-2xl p-12">
            <div className="grid md:grid-cols-3 gap-8 text-center text-white">
              <div>
                <p className="text-5xl font-bold mb-2 font-heading">50+</p>
                <p className="text-gray-200 text-lg">Projects Completed</p>
              </div>
              <div>
                <p className="text-5xl font-bold mb-2 font-heading">100%</p>
                <p className="text-gray-200 text-lg">Client Satisfaction</p>
              </div>
              <div>
                <p className="text-5xl font-bold mb-2 font-heading">24/7</p>
                <p className="text-gray-200 text-lg">Support Available</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};